import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Repeat, Star, ChevronRight } from 'lucide-react';
import api from '../lib/api';

/**
 * UpcomingWidget — compact slice of the user's calendar for Home + Profile.
 *
 * Props:
 *  - limit (number, default 4)
 *  - title (string, default "Upcoming")
 */
export default function UpcomingWidget({ limit = 4, title = 'Upcoming' }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/calendar/upcoming?limit=${limit}`)
      .then(r => setItems(r.data?.items || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [limit]);

  if (!loaded || items.length === 0) return null;

  return (
    <section data-testid="upcoming-widget" className="px-4 mb-5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-[#3F5200]" />
          <h3 className="text-sm font-extrabold text-[#111] uppercase tracking-wider">{title}</h3>
        </div>
        <button
          data-testid="upcoming-see-all"
          onClick={() => navigate('/calendar')}
          className="flex items-center gap-0.5 text-[11px] font-bold text-[#3F5200]"
        >
          See all <ChevronRight size={12} />
        </button>
      </div>
      <div className="space-y-2">
        {items.map((row, idx) => (
          <button
            key={`${row.plan.id}-${idx}`}
            data-testid={`upcoming-row-${row.plan.id}`}
            onClick={() => navigate(`/plan/${row.plan.id}`)}
            className="w-full flex items-center gap-3 bg-white rounded-2xl border border-black/[0.06] p-3 active:bg-[#F1F0EA]"
          >
            <div className="w-12 h-12 rounded-xl bg-[#B6E600]/15 text-[#3F5200] flex flex-col items-center justify-center flex-shrink-0">
              <span className="text-[9px] font-bold uppercase">{shortDay(row.date)}</span>
              <span className="text-base font-extrabold leading-none">{dayNumber(row.date)}</span>
            </div>
            <div className="flex-1 text-left min-w-0">
              <div className="flex items-center gap-1">
                <p className="text-sm font-extrabold text-[#111] truncate flex-1">{row.plan.title}</p>
                {row.is_host && <Star size={11} className="text-[#FFC107]" fill="currentColor" />}
                {row.is_recurring && <Repeat size={11} className="text-[#3F5200]" strokeWidth={2.6} />}
              </div>
              <p className="text-[11px] text-[#6B6E73] truncate">
                {(row.plan.start_time || '').slice(0, 5)} · {row.plan.venue || row.plan.city || ''}
              </p>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function shortDay(iso) {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString(undefined, { weekday: 'short' });
  } catch { return ''; }
}
function dayNumber(iso) {
  try {
    return new Date(iso + 'T00:00:00').getDate();
  } catch { return ''; }
}
